import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { ArrowLeft } from 'lucide-react';
import { trackEvent } from '../../lib/analytics';

const Privacy: React.FC = () => {
  useEffect(() => {
    trackEvent('legal_page', 'visit', 'privacy');
  }, []);

  return (
    <div className="min-h-screen bg-neutral-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/">
          <Button 
            variant="ghost" 
            size="sm" 
            className="mb-6"
            leftIcon={<ArrowLeft size={16} />}
            onClick={() => trackEvent('navigation', 'back_from_privacy', 'home')}
          >
            Back to Home
          </Button>
        </Link>
        
        <Card>
          <div className="p-6">
            <h1 className="text-2xl font-bold text-primary-800 font-display mb-6">Privacy Policy</h1>
            
            <div className="space-y-6 text-neutral-700">
              <section>
                <h2 className="text-xl font-semibold mb-3">1. Information We Collect</h2>
                <p className="mb-3">
                  To provide you with accurate astrological readings, we collect the following information:
                </p>
                <ul className="list-disc pl-6 mb-3 space-y-1">
                  <li>Phone number (for authentication)</li>
                  <li>Name</li>
                  <li>Gender</li>
                  <li>Email address (optional)</li>
                  <li>Date of birth</li>
                  <li>Time of birth</li>
                  <li>Place of birth</li>
                </ul>
                <p>
                  This information is essential for generating personalized astrological predictions and is stored securely.
                </p>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-3">2. How We Use Your Information</h2>
                <ul className="list-disc pl-6 space-y-1">
                  <li>To generate astrological readings and predictions</li>
                  <li>To authenticate your account</li>
                  <li>To process payments for premium subscriptions</li>
                  <li>To improve our services and user experience</li>
                  <li>To communicate important updates about our service</li>
                </ul>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-3">3. Data Security</h2>
                <p>
                  We implement industry-standard security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.
                </p>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-3">4. Third-Party Services</h2>
                <p>
                  We use third-party services for authentication (Firebase), database (Supabase), and payment processing (Razorpay). These services have their own privacy policies, and we encourage you to review them.
                </p>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-3">5. Your Rights</h2>
                <p>
                  You have the right to access, correct, or delete your personal information. You may also request a copy of the personal data we hold about you. To exercise these rights, please contact us at <a 
                    href="mailto:theaipanditji@gmail.com" 
                    className="text-primary-600 hover:underline"
                    onClick={() => trackEvent('user_engagement', 'email_click', 'privacy_contact')}
                  >theaipanditji@gmail.com</a>.
                </p>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-3">6. Changes to Privacy Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
                </p>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-3">7. Data Retention</h2>
                <p>
                  We retain your personal information for as long as your account is active or as needed to provide you with our services. If you wish to delete your account or request that we no longer use your information, please contact us at <a 
                    href="mailto:theaipanditji@gmail.com" 
                    className="text-primary-600 hover:underline"
                    onClick={() => trackEvent('user_engagement', 'email_click', 'privacy_data_retention')}
                  >theaipanditji@gmail.com</a>.
                </p>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-3">8. Contact Us</h2>
                <p>
                  If you have any questions about this Privacy Policy, please contact us at <a 
                    href="mailto:theaipanditji@gmail.com" 
                    className="text-primary-600 hover:underline"
                    onClick={() => trackEvent('user_engagement', 'email_click', 'privacy_general_contact')}
                  >theaipanditji@gmail.com</a>.
                </p>
              </section>
            </div>
            
            <div className="mt-8 text-sm text-neutral-500 text-center">
              Last Updated: June 10, 2025
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Privacy;